import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  StatTile,
  ScorePill,
  Delta,
  Tick,
  Sparkline,
  Chevron,
  AvatarChip,
  Pill,
  StatusChip,
  Empty,
  Loading,
  ErrorBox,
  HowCalculated,
  TH,
  TH_STICKY,
  TD,
  TD_BASE,
  TD_WRAP,
  fmtDate,
  fmtMins,
  fmtMinsFrom,
  ago,
} from './MetricsUI';
import Section from './Section';
import { buildCalendar, activitySummary } from './learnerDerive';
import { fetchLearnerOverview, fetchLearnerActivity, fetchModuleAttempts } from '../../apis/metrics.api';

const AVATARS = ['Stefan', 'AnneMarie', 'Ling', 'Tony', 'John'];
const SHADES = ['#eceff4', '#9be9a8', '#40c463', '#30a14e', '#216e39'];
const CELL = 11;
const GAP = 3;

/**
 * One learner's metrics. Used both for "my own progress" and, when a manager or
 * admin drills into a person, for that person — `targetUserId` is the only
 * difference, and the server decides whether the caller may see them.
 */
export default function LearnerMetrics({ idToken, targetUserId, targetUserIds, onBack, backLabel }) {
  const [overview, setOverview] = useState(null);
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setOverview(null);
    setError(null);
    Promise.all([
      fetchLearnerOverview(idToken, targetUserId, targetUserIds),
      fetchLearnerActivity(idToken, targetUserId, undefined, targetUserIds),
    ])
      .then(([o, a]) => {
        if (cancelled) return;
        setOverview(o);
        setActivity(a);
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
    // Joined for the dependency: a fresh array each render would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, targetUserId, (targetUserIds || []).join(','), reload]);

  // Derived once here rather than inside the calendar: the header tiles need the
  // same streak and total, and two copies would drift.
  const act = useMemo(() => activitySummary(activity && activity.days), [activity]);

  if (error) return <ErrorBox message={error} onRetry={() => setReload((r) => r + 1)} />;
  if (!overview) return <Loading />;

  /*
   * One continuous page: summary, then Content, then Case studies, then Quizzes.
   * There is no section nav — a sticky selector fought with the sticky table
   * headers further down the page, and the three sections are short enough to
   * scroll. Every section is mounted at once, which costs nothing extra: they
   * all read the one `overview` payload, and the only lazy fetch left is a case
   * study's or quiz's attempt history, which happens when a row is expanded.
   */
  return (
    <div className="space-y-10">
      {onBack && (
        <button onClick={onBack} className="text-sm text-lavender hover:underline">
          ← {backLabel || 'Back'}
        </button>
      )}

      <LevelHeader overview={overview} act={act} />
      <ContentSection overview={overview} activity={activity} act={act} />
      <CaseStudiesSection overview={overview} idToken={idToken} targetUserId={targetUserId} targetUserIds={targetUserIds} />
      <QuizzesSection overview={overview} idToken={idToken} targetUserId={targetUserId} targetUserIds={targetUserIds} />
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * level banner
 * ---------------------------------------------------------------- */

function LevelHeader({ overview, act }) {
  const { learner, level, scope, content, practice, quizzes } = overview;

  // "Level 2" -> "L2". Falls back to the raw name so an unexpected label still
  // renders something rather than an empty square.
  const badge = level.name ? level.name.replace(/^Level\s*/i, 'L') : '—';

  const scoredCases = practice.filter((p) => p.latestScore);
  const avgCaseScore = scoredCases.length
    ? Math.round(scoredCases.reduce((s, p) => s + p.latestScore.pct, 0) / scoredCases.length)
    : null;
  const quizzesDone = quizzes.filter((q) => q.finished).length;

  // Most recent thing they actually did, across both module types.
  const lastActive = [...practice, ...quizzes].reduce(
    (max, m) => (m.lastPlayedAt && m.lastPlayedAt > max ? m.lastPlayedAt : max),
    0
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-navy p-6 text-white shadow">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-lavender text-2xl font-bold">
              {badge}
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-indigo-200">Learning path</p>
              <h1 className="text-2xl font-bold leading-tight">{level.name || 'Level not set'}</h1>
              <p className="mt-0.5 text-sm text-indigo-200">
                {level.assessment ? (
                  <>
                    Set by the intro assessment on{' '}
                    {fmtDate(new Date(level.assessment.takenAt).getTime())} ·{' '}
                    {level.assessment.totalPoints} points
                  </>
                ) : (
                  'No intro assessment on record — the default path is in use'
                )}
              </p>
            </div>
          </div>

          <div className="hidden h-14 w-px bg-white/15 lg:block" />

          <div className="min-w-[240px] flex-1">
            {/*
              * Counted the way the tiles count.
              *
              * This used to read scope.chaptersInScope, which includes chapters that
              * hold nothing to do, while the "chapters complete" tile below divides by
              * the ones that do — so the header said 41 and the tile said 40 about the
              * same path. The in-path number now comes from the same summary the tile
              * uses, and the total is that plus the level's exclusions, so the two
              * cannot disagree.
              *
              * The level warnings are deliberately not shown. The only one that fired
              * in practice was levelNotEnabledForOrg, which says the organisation does
              * not force the intro assessment — true, but nothing to do with what
              * counts, and it read as though the figures beside it were unreliable.
              */}
            <p className="text-sm text-indigo-100">
              The level decides what counts.{' '}
              <b className="text-white">{content.summary.inScope}</b> of the{' '}
              {content.summary.inScope + scope.excludedByLevel} chapters are in this path — the other{' '}
              {scope.excludedByLevel} are excluded and are not held against progress.
            </p>
          </div>

          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-indigo-200">Learner</p>
            {/* displayLabel is always sent. When the viewing manager has
                anonymous metrics on and is looking at someone else, `name` and
                `email` are absent from the payload entirely, so there is nothing
                here to hide — the label is all that arrived. */}
            <p className="font-semibold">{learner.displayLabel || learner.name || learner.email}</p>
            {learner.email && <p className="text-sm text-indigo-200">{learner.email}</p>}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <StatTile
          label="Chapters complete"
          value={content.chaptersComplete}
          unit={`/ ${content.chaptersInScope}`}
          hint={`${
            content.chaptersInScope
              ? Math.round((100 * content.chaptersComplete) / content.chaptersInScope)
              : 0
          }% of this path`}
        />
        <StatTile
          label="Time in training"
          value={(act.totalMinutes / 60).toFixed(1)}
          unit="h"
          hint={`${act.activeDays} active days`}
        />
        <StatTile
          label="Current streak"
          value={act.currentStreak}
          unit="days"
          tone={act.currentStreak > 0 ? 'good' : 'default'}
          hint={`longest ${act.longestStreak}`}
        />
        <StatTile
          label="Avg case study score"
          value={avgCaseScore == null ? '—' : `${avgCaseScore}%`}
          tone="accent"
          hint={`${scoredCases.length} of ${practice.length} scored`}
        />
        <StatTile label="Quizzes done" value={quizzesDone} unit={`/ ${quizzes.length}`} />
        <StatTile
          label="Last active"
          value={lastActive ? fmtDate(lastActive).replace(/, \d{4}$/, '') : 'Never'}
          hint={lastActive ? ago(lastActive) : null}
        />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Content section
 * ---------------------------------------------------------------- */

function ContentSection({ overview, activity, act }) {
  /*
   * status, percent and the totals all come from the server now. They used to be
   * derived here, which meant a manager looking at this learner and the learner
   * themself were running two different implementations of "chapter complete".
   *
   * Chapters with nothing in them are dropped from the table: on the live
   * content that is Chapter 42 "Case Studies", the Negotiation Challenges menu,
   * which has its own section further down the page. The count is reported under
   * the table rather than vanishing silently.
   */
  const rows = useMemo(
    () => overview.content.chapters.filter((c) => c.hasContent !== false),
    [overview]
  );
  const summary = overview.content.summary;

  /*
   * The ring is the summary; the calendar and the chapter table are the detail.
   *
   * "Where am I in the course" is one number and one shape, and that is what
   * belongs on an unopened page. The 53-week calendar and a 41-row table are
   * both worth having and neither is worth scrolling past to reach the next
   * section — so they sit behind the heading's chevron.
   */
  return (
    <Section
      title="Content"
      subtitle="Where they are in the course, and how consistently they show up"
      summary={
        <div className="max-w-[520px]">
          <ChapterProgress summary={summary} level={overview.level} scope={overview.scope} />
        </div>
      }
    >
      <ActivityCalendar activity={activity} summary={act} />
      <ChapterBreakdown rows={rows} withoutContent={summary.withoutContent} />
    </Section>
  );
}

function ChapterProgress({ summary, level, scope }) {
  const { complete, inProgress, notStarted, inScope, percent } = summary;
  const R = 78;
  const C = 2 * Math.PI * R;
  const seg = (n) => (inScope ? (n / inScope) * C : 0);

  const arcs = [
    { label: 'Complete', value: complete, color: '#16a34a' },
    { label: 'In progress', value: inProgress, color: '#6c63ff' },
    { label: 'Not started', value: notStarted, color: '#e5e7eb' },
  ];

  let offset = 0;
  const rendered = arcs.map((a) => {
    const len = seg(a.value);
    const el = (
      <circle
        key={a.label}
        cx="100"
        cy="100"
        r={R}
        fill="none"
        stroke={a.color}
        strokeWidth="20"
        strokeDasharray={`${len} ${C - len}`}
        strokeDashoffset={-offset}
        transform="rotate(-90 100 100)"
      />
    );
    offset += len;
    return el;
  });

  return (
    <Card className="flex h-full flex-col">
      <div>
        <h3 className="text-lg font-semibold text-navy">
          Chapter progress
          <HowCalculated>
            A chapter counts as complete when every lesson in it has been watched to the end, every quiz has
            been attempted, and at least one of its case studies has been tried. There is no pass mark on
            quizzes — the same rule the app uses. Chapters excluded by the learning-path level are removed
            from the denominator entirely, so the total reflects only what is required at this level.
          </HowCalculated>
        </h3>
        <p className="mt-0.5 text-xs text-gray-500">
          {inScope} chapters in the {level.name || 'default'} path · {scope.excludedByLevel} not required at
          this level
        </p>
      </div>

      <div className="mt-4 flex flex-1 items-center gap-6">
        <div className="relative shrink-0">
          <svg width="200" height="200" viewBox="0 0 200 200">
            {rendered}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-4xl font-bold tabular-nums text-navy">{percent}%</span>
            <span className="mt-0.5 text-xs text-gray-500">
              {complete} of {inScope} done
            </span>
          </div>
        </div>

        <div className="flex-1 space-y-3">
          {arcs.map((a) => (
            <div key={a.label} className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: a.color }} />
              <span className="flex-1 text-sm text-gray-600">{a.label}</span>
              <span className="text-sm font-semibold tabular-nums text-navy">{a.value}</span>
            </div>
          ))}

          <div className="mt-1 space-y-2 border-t border-gray-100 pt-3">
            <MiniStat label="Lessons watched" done={summary.lessonsDone} total={summary.lessonsTotal} />
            <MiniStat label="Quizzes done" done={summary.quizzesDone} total={summary.quizzesTotal} />
          </div>
        </div>
      </div>
    </Card>
  );
}

function MiniStat({ label, done, total }) {
  const p = total ? Math.round((100 * done) / total) : 0;
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="font-semibold tabular-nums text-navy">
          {done}/{total}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-lavender" style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

function ActivityCalendar({ activity, summary }) {
  const [hover, setHover] = useState(null);
  const weeks = useMemo(() => buildCalendar(activity && activity.days), [activity]);

  if (!activity) {
    return (
      <Card>
        <Loading what="activity" />
      </Card>
    );
  }

  // Month labels: first column of each new month, skipping any that would collide.
  const monthLabels = [];
  let lastMonth = -1;
  let lastAt = -99;
  weeks.forEach((col, i) => {
    const mo = col[0].date.getMonth();
    if (mo !== lastMonth && i - lastAt >= 3) {
      monthLabels.push({ i, label: col[0].date.toLocaleDateString('en-US', { month: 'short' }) });
      lastAt = i;
    }
    lastMonth = mo;
  });

  const width = weeks.length * (CELL + GAP);

  return (
    <Card className="flex h-full flex-col">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-navy">
            Learning activity
            <HowCalculated>
              Shade is total time in the app that day — light up to 15 minutes, then 35, then 60, then more.
              Built from module start and end times, so it reflects real time spent in lessons, quizzes and
              case studies rather than logins. Days are bucketed in your own timezone ({activity.timezone}).
            </HowCalculated>
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {summary.activeDays} active days in the last 12 months ·{' '}
            {(summary.totalMinutes / 60).toFixed(0)}h total
          </p>
        </div>
        <div className="flex gap-4 text-right">
          <div>
            <p className="text-xs text-gray-500">Current streak</p>
            <p className="text-xl font-bold tabular-nums text-navy">
              {summary.currentStreak}
              <span className="ml-1 text-xs font-medium text-gray-400">days</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Longest</p>
            <p className="text-xl font-bold tabular-nums text-navy">
              {summary.longestStreak}
              <span className="ml-1 text-xs font-medium text-gray-400">days</span>
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto pb-1">
        <div style={{ width, minWidth: width }} className="relative">
          <div className="relative mb-1 h-4">
            {monthLabels.map((m) => (
              <span
                key={`${m.label}-${m.i}`}
                className="absolute text-[10px] font-medium text-gray-400"
                style={{ left: m.i * (CELL + GAP) }}
              >
                {m.label}
              </span>
            ))}
          </div>
          <div className="flex" style={{ gap: GAP }}>
            {weeks.map((col, ci) => (
              <div key={ci} className="flex flex-col" style={{ gap: GAP }}>
                {col.map((cell) => (
                  <div
                    key={cell.key}
                    onMouseEnter={() => !cell.future && setHover(cell)}
                    onMouseLeave={() => setHover(null)}
                    className="rounded-[2px]"
                    style={{
                      width: CELL,
                      height: CELL,
                      background: cell.future ? 'transparent' : SHADES[cell.intensity],
                      outline: hover && hover.key === cell.key ? '1.5px solid #071146' : 'none',
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fixed height so the card does not jump as the pointer moves. */}
      <div className="mt-3 flex h-10 items-center">
        {hover ? (
          <div className="text-sm">
            <span className="font-semibold text-navy">{fmtDate(hover.date.getTime())}</span>
            {hover.modules ? (
              <span className="text-gray-600">
                {' '}
                — {fmtMinsFrom(hover.minutes)} across {hover.modules} module
                {hover.modules === 1 ? '' : 's'}
                <span className="text-gray-400">
                  {' '}
                  ({hover.lessons} lesson{hover.lessons === 1 ? '' : 's'}, {hover.quizzes} quiz
                  {hover.quizzes === 1 ? '' : 'zes'}, {hover.cases} case stud
                  {hover.cases === 1 ? 'y' : 'ies'})
                </span>
              </span>
            ) : (
              <span className="text-gray-400"> — no activity</span>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400">Hover a square for that day&rsquo;s detail.</p>
        )}
      </div>

      <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-4 text-xs text-gray-500">
          <span>
            Last 30 days: <b className="text-navy">{summary.daysLast30} days</b>
          </span>
          <span>
            Avg per active day:{' '}
            <b className="text-navy">{fmtMinsFrom(summary.avgMinutesPerActiveDay)}</b>
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
          <span>Less</span>
          {SHADES.map((s) => (
            <span key={s} className="rounded-[2px]" style={{ width: CELL, height: CELL, background: s }} />
          ))}
          <span>More</span>
        </div>
      </div>
    </Card>
  );
}

function ChapterBreakdown({ rows, withoutContent = 0 }) {
  // Collapsed by default, as in the prototype: the ring and the calendar above
  // are the summary, and a 42-row table unrolled underneath buries them.
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const shown = rows.filter((c) => filter === 'all' || c.status === filter);

  return (
    <Card>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left">
        <div>
          <h3 className="text-lg font-semibold text-navy">Chapter-by-chapter breakdown</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            All {rows.length} chapters in this path, with what is still outstanding in each
          </p>
        </div>
        <Chevron open={open} />
      </button>

      {open && (
        <div className="mt-4">
          <div className="mb-3 flex gap-2">
            {[
              ['all', 'All'],
              ['in-progress', 'In progress'],
              ['not-started', 'Not started'],
              ['complete', 'Complete'],
            ].map(([k, label]) => (
              <Pill key={k} active={filter === k} onClick={() => setFilter(k)}>
                {label}
              </Pill>
            ))}
          </div>

          <div className="max-h-[420px] overflow-y-auto rounded-md border border-gray-100">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`${TH_STICKY} w-14 ${TH}`}>
                    Ch.
                  </th>
                  <th className={`${TH_STICKY} ${TH}`}>
                    Chapter
                  </th>
                  <th className={`${TH_STICKY} w-28 ${TH}`}>
                    Lessons
                  </th>
                  <th className={`${TH_STICKY} w-24 ${TH}`}>
                    Quizzes
                  </th>
                  <th className={`${TH_STICKY} w-28 ${TH}`}>
                    Case studies
                  </th>
                  <th className={`${TH_STICKY} w-40 ${TH}`}>
                    Progress
                  </th>
                  <th className={`${TH_STICKY} w-28 ${TH}`}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shown.map((c) => (
                  <tr key={c.chapterId} className="hover:bg-gray-50/70">
                    <td className={`${TD_BASE} tabular-nums text-gray-400`}>{c.number}</td>
                    {/* TD_WRAP, not TD_BASE: chapter titles are long and must
                        wrap rather than force the scroller sideways. */}
                    <td className={`${TD_WRAP} font-medium text-navy`}>{c.title}</td>
                    <Fraction done={c.lessonsDone} total={c.lessonsTotal} />
                    <Fraction done={c.quizzesDone} total={c.quizzesTotal} />
                    <Fraction done={c.casesAttempted} total={c.casesTotal} />
                    <td className={TD}>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className={`h-full rounded-full ${c.complete ? 'bg-emerald-500' : 'bg-lavender'}`}
                            style={{ width: `${c.percent}%` }}
                          />
                        </div>
                        <span className="w-9 text-right text-xs tabular-nums text-gray-500">{c.percent}%</span>
                      </div>
                    </td>
                    <td className={TD}>
                      <StatusChip status={c.status} />
                    </td>
                  </tr>
                ))}
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <Empty>No chapters in this state.</Empty>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {withoutContent > 0 && (
            <p className="mt-2 text-xs text-gray-400">
              {withoutContent} chapter{withoutContent === 1 ? '' : 's'} in this path hold no lessons, quizzes
              or case studies and {withoutContent === 1 ? 'is' : 'are'} left out of the totals — the Case
              Studies menu is one of them, and it has its own section below.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function Fraction({ done, total }) {
  if (!total) return <td className={`${TD_BASE} text-gray-300`}>—</td>;
  return (
    <td className={`${TD} tabular-nums`}>
      <span className={done === total ? 'font-semibold text-emerald-600' : 'text-gray-600'}>
        {done}/{total}
      </span>
    </td>
  );
}

/* ---------------------------------------------------------------- *
 * Case studies section
 * ---------------------------------------------------------------- */

function CaseStudiesSection({ overview, idToken, targetUserId, targetUserIds }) {
  // Every row starts collapsed. Auto-opening the first one fired an attempt-
  // history request nobody asked for and made the table look stuck open.
  const [openId, setOpenId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const { practice, tracking } = overview;

  const rows = showAll ? practice : practice.filter((p) => p.attempts > 0);
  const scored = practice.filter((p) => p.latestScore);
  const improvements = practice.map((p) => p.improvement).filter((v) => v !== null);
  const avgScore = scored.length ? scored.reduce((s, p) => s + p.latestScore.pct, 0) / scored.length : null;
  const avgImp = improvements.length ? improvements.reduce((s, v) => s + v, 0) / improvements.length : null;

  /*
   * Avatar coverage: how many case-study x avatar pairs have actually been
   * played, out of every pair on offer. Counted only over case studies that
   * report avatar data at all, so historical rows that predate the tracking do
   * not drag the denominator up and make coverage look worse than it is.
   */
  const withAvatars = practice.filter((p) => p.avatarBreakdown);
  const coverage = withAvatars.reduce(
    (s, p) => s + p.avatarBreakdown.filter((a) => a.attempts > 0).length,
    0
  );
  const coverageMax = withAvatars.reduce((s, p) => s + p.avatarBreakdown.length, 0);

  /*
   * Total time. avgMs is the mean over the attempts that HAD a usable duration,
   * and avgMsBasis.n says how many those were — so their product is the exact
   * sum of the timed attempts, not an estimate over all of them.
   */
  const totalMs = practice.reduce(
    (s, p) => s + (p.avgMs && p.avgMsBasis ? p.avgMs * p.avgMsBasis.n : 0),
    0
  );

  /*
   * Tiles are the summary; the table is the detail.
   *
   * "How am I doing on the case studies" is five numbers. The table underneath
   * is 20 rows with an expandable per-avatar panel inside each, which is a lot
   * to scroll past on the way to the quizzes.
   */
  return (
    <Section
      divider
      title="Case studies"
      subtitle="Negotiation Challenges — every case study you can run against five AI avatars"
      summary={
        <div className="flex flex-wrap gap-4">
        <StatTile
          label="Case studies attempted"
          value={practice.filter((p) => p.attempts > 0).length}
          unit={`/ ${practice.length}`}
        />
        {coverageMax > 0 && (
          <StatTile
            label="AI avatars faced"
            value={coverage}
            unit={`/ ${coverageMax}`}
            hint="case study × avatar pairs"
            help="Each AI avatar negotiates differently, so the same case study against a different avatar is a genuinely different rehearsal. This counts the pairs actually played."
          />
        )}
        <StatTile
          label="Avg latest score"
          value={avgScore == null ? '—' : `${Math.round(avgScore)}%`}
          tone="accent"
        />
        <StatTile
          label="Avg improvement"
          value={avgImp == null ? 'n/a' : `${avgImp > 0 ? '+' : ''}${Math.round(avgImp)} pts`}
          tone={avgImp > 0 ? 'good' : 'default'}
          hint={`over ${improvements.length} case stud${improvements.length === 1 ? 'y' : 'ies'}`}
          help={
            <>
              A rolling average, not first-versus-last: the mean of the last three attempts minus the mean of
              the first three, in percentage points. With fewer than six attempts the window shrinks so the
              same attempt is never counted on both sides; with fewer than two it reads &ldquo;n/a&rdquo;.
              Scores are normalised to each case study&rsquo;s own maximum first, because rubrics differ and
              get revised.
            </>
          }
        />
        <StatTile label="Total attempts" value={practice.reduce((s, p) => s + p.attempts, 0)} />
          <StatTile
            label="Time negotiating"
            value={(totalMs / 3600000).toFixed(1)}
            unit="h"
            help="Summed over the attempts that recorded both a start and an end. Attempts that ended without one — a crash, or a closed tab before the beacon landed — contribute nothing rather than a guess."
          />
        </div>
      }
    >
      <Card className="overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-medium text-gray-600">{rows.length} case studies</p>
          <label className="flex cursor-pointer select-none items-center gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="accent-lavender"
            />
            Show ones not started
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8" />
                <th className={TH}>
                  Case study
                </th>
                <th className="w-16 px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Done
                </th>
                {tracking.avatar.available && (
                  <th className={`w-40 ${TH}`}>
                    AI avatars faced
                  </th>
                )}
                <th className={`w-24 ${TH}`}>
                  Latest
                </th>
                <th className={`w-20 ${TH}`}>
                  Best
                </th>
                <th className={`w-32 ${TH}`}>
                  Improvement
                </th>
                <th className={`w-28 ${TH}`}>
                  Score trend
                </th>
                <th className={`w-24 ${TH}`}>
                  Avg time
                </th>
                <th className={`w-20 ${TH}`}>
                  Tries
                </th>
                <th className={`w-28 ${TH}`}>
                  Last played
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <React.Fragment key={row.moduleIdSst}>
                  <tr
                    onClick={() => setOpenId(openId === row.moduleIdSst ? null : row.moduleIdSst)}
                    className={`cursor-pointer ${
                      openId === row.moduleIdSst ? 'bg-indigo-50/50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="pl-3">
                      <Chevron open={openId === row.moduleIdSst} />
                    </td>
                    <td className={TD_WRAP}>
                      <p className="text-sm font-semibold text-navy">{row.moduleName}</p>
                      <p className="text-xs text-gray-400">
                        Chapter {row.chapterNumber} · {row.chapterTitle}
                      </p>
                    </td>
                    <td className="px-2 py-3 text-center text-sm text-gray-700 whitespace-nowrap">
                      <Tick state={row.completed} />
                    </td>
                    {tracking.avatar.available && (
                      <td className={TD}>
                        <div className="flex items-center gap-1.5">
                          {AVATARS.map((a) => {
                            const seen =
                              row.avatarBreakdown && row.avatarBreakdown.find((x) => x.avatarName === a);
                            return <AvatarChip key={a} name={a} muted={!seen || seen.attempts === 0} />;
                          })}
                          {row.avatarBreakdown && (
                            <span className="ml-1 text-xs tabular-nums text-gray-500">
                              {row.avatarBreakdown.filter((x) => x.attempts > 0).length}/
                              {row.avatarBreakdown.length}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className={TD}>
                      <ScorePill pct={row.latestScore && row.latestScore.pct} />
                    </td>
                    <td className={`${TD_BASE} tabular-nums text-gray-500`}>
                      {row.bestScore ? `${Math.round(row.bestScore.pct)}%` : '—'}
                    </td>
                    <td className={TD}>
                      <Delta value={row.improvement} />
                    </td>
                    <td className={TD}>
                      <Sparkline points={row.scoreTrend} />
                    </td>
                    <td className={`${TD_BASE} text-gray-600`}>{fmtMins(row.avgMs)}</td>
                    <td className={`${TD_BASE} tabular-nums text-gray-600`}>{row.attempts || '—'}</td>
                    <td className={`${TD_BASE} text-gray-500`}>{ago(row.lastPlayedAt)}</td>
                  </tr>
                  {openId === row.moduleIdSst && (
                    <tr>
                      <td
                        colSpan={tracking.avatar.available ? 11 : 10}
                        className="border-t border-gray-100 bg-gray-50 px-6 py-5"
                      >
                        <CaseStudyDetail row={row} idToken={idToken} targetUserId={targetUserId} targetUserIds={targetUserIds} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11}>
                    <Empty>No case studies attempted yet.</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Section>
  );
}

/**
 * Expanded case study: one card per AI avatar, each carrying that avatar's own
 * attempt list. Grouping by avatar rather than one flat list is the point of the
 * section — the useful question is "how am I doing against Tony", not "what
 * happened on the 14th".
 */
function CaseStudyDetail({ row, idToken, targetUserId, targetUserIds }) {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchModuleAttempts(idToken, row.moduleIdSst, targetUserId, targetUserIds)
      .then((d) => !cancelled && setDetail(d))
      .catch((e) => !cancelled && setErr(e.message));
    return () => {
      cancelled = true;
    };
  // targetUserIds is read inside; a fresh array each render would refetch
  // forever, so the id is the dependency and the set rides along with it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, row.moduleIdSst, targetUserId]);

  const grouped = useMemo(() => {
    if (!detail) return null;
    const map = new Map(AVATARS.map((a) => [a, []]));
    detail.attempts.forEach((a) => {
      if (!a.avatarName) return;
      if (!map.has(a.avatarName)) map.set(a.avatarName, []);
      map.get(a.avatarName).push(a);
    });
    return map;
  }, [detail]);

  if (err) return <ErrorBox message={err} />;
  if (!detail) return <Loading what="attempts" />;

  const summaries = detail.avatarBreakdown || [];
  const faced = summaries.filter((a) => a.attempts > 0);
  const unfaced = summaries.filter((a) => a.attempts === 0);

  // No avatar on any attempt — this case study predates that tracking.
  if (faced.length === 0) return <FlatAttempts attempts={detail.attempts} />;

  return (
    <div className="space-y-5">
      <h4 className="mb-3 text-sm font-semibold text-navy">Performance by AI avatar</h4>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {faced.map((a) => {
          const attempts = (grouped.get(a.avatarName) || []).filter((x) => x.scored);
          return (
            <div key={a.avatarName} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <div className="mb-3 flex items-center gap-2.5">
                <AvatarChip name={a.avatarName} size="lg" />
                <div className="flex-1">
                  <p className="font-semibold leading-tight text-navy">{a.avatarName}</p>
                  <p className="text-xs text-gray-400">
                    {a.attempts} attempt{a.attempts === 1 ? '' : 's'} · last {ago(a.lastPlayedAt)}
                  </p>
                </div>
                <ScorePill pct={a.latestScore && a.latestScore.pct} size="lg" />
              </div>

              <div className="flex items-end justify-between">
                <div className="grid flex-1 grid-cols-3 gap-3 text-xs">
                  <div>
                    <p className="text-gray-400">Best</p>
                    <p className="font-semibold tabular-nums text-navy">
                      {a.bestScore ? `${Math.round(a.bestScore.pct)}%` : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Change</p>
                    <Delta value={a.improvement} />
                  </div>
                  <div>
                    <p className="text-gray-400">Avg time</p>
                    <p className="font-semibold text-navy">{fmtMins(a.avgMs)}</p>
                  </div>
                </div>
                <Sparkline points={attempts.map((x) => Math.round(x.pct))} width={80} />
              </div>

              <ul className="mt-3 space-y-1 border-t border-gray-100 pt-3">
                {attempts.map((at, i) => (
                  <li key={at.instanceGuid} className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      #{i + 1} · {fmtDate(at.startedAt)}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-gray-400">{fmtMins(at.durationMs)}</span>
                      <span className="w-9 text-right font-semibold tabular-nums text-navy">
                        {Math.round(at.pct)}%
                      </span>
                    </span>
                  </li>
                ))}
                {attempts.length === 0 && (
                  <li className="text-xs italic text-gray-400">No completed attempts.</li>
                )}
              </ul>
            </div>
          );
        })}

        {unfaced.length > 0 && (
          <div className="flex flex-col justify-center rounded-lg border-2 border-dashed border-gray-200 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Not yet faced ({unfaced.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {unfaced.map((a) => (
                <span key={a.avatarName} className="flex items-center gap-1.5 text-sm text-gray-500">
                  <AvatarChip name={a.avatarName} muted />
                  {a.avatarName}
                </span>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-400">
              Each AI avatar negotiates differently — facing all five is the fastest way to lift the score on
              this case.
            </p>
          </div>
        )}
      </div>

      {/*
        The per-avatar cards answer "how am I doing against Tony". This answers
        "what actually happened, in order" — including the attempts that carry no
        avatar, which the cards above cannot show at all.
      */}
      <FlatAttempts attempts={detail.attempts} showAvatar title="Full attempt history" />
    </div>
  );
}

/*
 * Every attempt at one module, newest first, paginated.
 *
 * Used twice: under the per-avatar cards as the combined history, and on its own
 * for case studies whose attempts all predate avatar tracking. Paginated because
 * a well-practised case study can carry 30+ attempts and the expanded row sits
 * inside a table — the prototype's unbounded list pushed everything off screen.
 */
const PAGE = 10;

function FlatAttempts({ attempts, showAvatar, title = 'Attempt history' }) {
  const [shown, setShown] = useState(PAGE);

  // Number in chronological order, then show newest first — so "#1" is always
  // the first ever attempt regardless of which way the list is read.
  const ordered = attempts.map((a, i) => ({ ...a, n: i + 1 })).reverse();
  const visible = ordered.slice(0, shown);
  const remaining = ordered.length - visible.length;

  // vs-previous compares against the previous SCORED attempt, not the previous
  // row: an abandoned attempt in between has no score, and treating it as one
  // would invent a drop that never happened.
  const scoredAsc = attempts.filter((a) => a.scored);
  const prevScored = (a) => scoredAsc.filter((s) => s.startedAt < a.startedAt).pop();

  if (!attempts.length) return <Empty>No attempts recorded.</Empty>;

  const th = 'px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <h4 className="text-sm font-semibold text-navy">{title}</h4>
        <span className="text-xs text-gray-400">
          showing {visible.length} of {ordered.length}, most recent first
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg bg-white shadow-sm ring-1 ring-gray-100">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className={`w-12 ${th}`}>#</th>
              <th className={th}>Date</th>
              {showAvatar && <th className={th}>AI avatar</th>}
              <th className={th}>Duration</th>
              <th className={th}>Score</th>
              <th className={`w-36 ${th}`}>vs previous</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((a) => {
              const prev = prevScored(a);
              return (
                <tr key={a.instanceGuid} className={a.scored ? '' : 'text-gray-400'}>
                  <td className="px-4 py-2 text-sm tabular-nums text-gray-400 whitespace-nowrap">{a.n}</td>
                  <td className="px-4 py-2 text-sm whitespace-nowrap">{fmtDate(a.startedAt)}</td>
                  {showAvatar && (
                    <td className="px-4 py-2 text-sm whitespace-nowrap">
                      {a.avatarName ? (
                        <span className="flex items-center gap-2">
                          <AvatarChip name={a.avatarName} />
                          {a.avatarName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">not recorded</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                    {fmtMins(a.durationMs)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {a.scored ? (
                      <ScorePill pct={a.pct} />
                    ) : (
                      <span className="text-xs italic">not finished</span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {!a.scored ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : !prev ? (
                      <span className="text-xs text-gray-400">first attempt</span>
                    ) : (
                      <Delta value={a.pct - prev.pct} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {remaining > 0 && (
        <button
          onClick={() => setShown((n) => n + PAGE)}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white py-2 text-xs font-medium text-lavender hover:border-lavender hover:bg-indigo-50/40"
        >
          <span className="text-base leading-none">⌄</span>
          Show {Math.min(PAGE, remaining)} more
          <span className="text-gray-400">({remaining} older)</span>
        </button>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Quizzes section
 * ---------------------------------------------------------------- */

function QuizzesSection({ overview, idToken, targetUserId, targetUserIds }) {
  const { quizzes } = overview;
  const [openId, setOpenId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const attempted = quizzes.filter((q) => q.attempts > 0);
  const rows = showAll ? quizzes : attempted;

  const finished = attempted.filter((q) => q.finished).length;
  const retried = attempted.filter((q) => q.attempts > 1).length;
  // Scores are suppressed where they cannot be believed, so the score averages
  // run over the trusted subset only and say how big that subset was.
  const trusted = attempted.filter((q) => q.scoreIsTrusted);
  const mean = (xs) => (xs.length ? xs.reduce((s, v) => s + v, 0) / xs.length : null);
  const avgLatest = mean(trusted.filter((q) => q.latestScore).map((q) => q.latestScore.pct));
  const avgBest = mean(trusted.filter((q) => q.bestScore).map((q) => q.bestScore.pct));
  const improvements = quizzes.map((q) => q.improvement).filter((v) => v !== null && v !== undefined);
  const avgImp = mean(improvements);

  const ROLLING = (
    <>
      A rolling average, not first-versus-last: the mean of the last three attempts minus the mean of the
      first three, in percentage points. With fewer than six attempts the window shrinks so the same attempt
      is never counted on both sides; with fewer than two it reads &ldquo;n/a&rdquo;.
    </>
  );

  return (
    <Section
      divider
      title="Quizzes"
      subtitle="Knowledge checks across the chapters in this path — every attempt kept, no pass mark"
      summary={
        <div className="flex flex-wrap gap-4">
        <StatTile label="Quizzes attempted" value={attempted.length} unit={`/ ${quizzes.length}`} />
        <StatTile
          label="Finished"
          value={finished}
          unit={`/ ${attempted.length}`}
          tone="good"
          help={
            <>
              There is no pass mark. A quiz counts as done once it has been attempted and submitted, which is
              exactly how the app treats it — so the app and this page can never disagree about whether a
              chapter is finished. Scores are still recorded and charted; they measure improvement, not
              eligibility.
            </>
          }
        />
        <StatTile
          label="Avg latest score"
          value={avgLatest == null ? '—' : `${Math.round(avgLatest)}%`}
          tone="accent"
          hint={
            trusted.length < attempted.length ? `${trusted.length} of ${attempted.length} scoreable` : null
          }
          help={
            <>
              Averaged over the quizzes whose recorded score can be trusted. Where it cannot — a 1-out-of-1
              that would render as a confident 100% — the score shows &ldquo;—&rdquo; and is left out of this
              average, while the quiz still counts as done.
            </>
          }
        />
        <StatTile label="Avg best score" value={avgBest == null ? '—' : `${Math.round(avgBest)}%`} />
        <StatTile
          label="Retaken at least once"
          value={retried}
          hint={`of ${attempted.length} attempted`}
        />
        <StatTile
          label="Avg improvement"
          value={avgImp == null ? 'n/a' : `${avgImp > 0 ? '+' : ''}${Math.round(avgImp)} pts`}
          tone={avgImp > 0 ? 'good' : 'default'}
          hint={`across ${improvements.length} retaken quiz${improvements.length === 1 ? '' : 'zes'}`}
            help={ROLLING}
          />
        </div>
      }
    >
      <Card className="overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-medium text-gray-600">
            {rows.length} quiz{rows.length === 1 ? '' : 'zes'}
          </p>
          <label className="flex cursor-pointer select-none items-center gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="accent-lavender"
            />
            Show ones not taken
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8" />
                <th className={TH}>Quiz</th>
                <th className={`w-20 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500`}>
                  Done
                </th>
                <th className={`w-20 ${TH}`}>Tries</th>
                <th className={`w-24 ${TH}`}>Latest</th>
                <th className={`w-20 ${TH}`}>Best</th>
                <th className={`w-32 ${TH}`}>Improvement</th>
                <th className={`w-28 ${TH}`}>Score trend</th>
                <th className={`w-28 ${TH}`}>Last taken</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((q) => (
                <React.Fragment key={q.moduleIdSst}>
                  <tr
                    onClick={() => setOpenId(openId === q.moduleIdSst ? null : q.moduleIdSst)}
                    className={`cursor-pointer transition-colors ${
                      openId === q.moduleIdSst ? 'bg-indigo-50/50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="pl-3">
                      <Chevron open={openId === q.moduleIdSst} />
                    </td>
                    <td className={TD_WRAP}>
                      <p className="text-sm font-semibold text-navy">{q.moduleName}</p>
                      <p className="text-xs text-gray-400">
                        Chapter {q.chapterNumber} · {q.chapterTitle}
                      </p>
                    </td>
                    <td className={`${TD} text-center`}>
                      <Tick
                        state={q.attempts > 0 ? q.finished : null}
                        title={
                          q.attempts === 0
                            ? 'Not taken yet'
                            : q.finished
                              ? 'Attempted and submitted'
                              : 'Not finished'
                        }
                      />
                    </td>
                    <td className={`${TD_BASE} tabular-nums text-gray-600`}>{q.attempts || '—'}</td>
                    <td className={TD}>
                      <ScorePill pct={q.latestScore && q.latestScore.pct} />
                    </td>
                    <td className={`${TD_BASE} font-medium tabular-nums text-gray-500`}>
                      {q.bestScore ? `${Math.round(q.bestScore.pct)}%` : '—'}
                    </td>
                    <td className={TD}>
                      <Delta value={q.improvement} />
                    </td>
                    <td className={TD}>
                      <Sparkline points={q.scoreTrend} />
                    </td>
                    <td className={`${TD_BASE} text-gray-500`}>{ago(q.lastPlayedAt)}</td>
                  </tr>
                  {openId === q.moduleIdSst && (
                    <tr>
                      <td colSpan={9} className="border-t border-gray-100 bg-gray-50 px-6 py-5">
                        <QuizDetail quiz={q} idToken={idToken} targetUserId={targetUserId} targetUserIds={targetUserIds} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9}>
                    <Empty>No quizzes attempted yet.</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Section>
  );
}

/**
 * Expanded quiz: every attempt, plus a summary panel.
 *
 * There is no pass/fail column. The app treats a submitted quiz as finished and
 * this page has to agree, so the per-attempt question is "what did you score",
 * not "did you clear a bar".
 */
function QuizDetail({ quiz, idToken, targetUserId, targetUserIds }) {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchModuleAttempts(idToken, quiz.moduleIdSst, targetUserId, targetUserIds)
      .then((d) => !cancelled && setDetail(d))
      .catch((e) => !cancelled && setErr(e.message));
    return () => {
      cancelled = true;
    };
  // targetUserIds is read inside; a fresh array each render would refetch
  // forever, so the id is the dependency and the set rides along with it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, quiz.moduleIdSst, targetUserId]);

  if (err) return <ErrorBox message={err} />;
  if (!detail) return <Loading what="attempts" />;

  const attempts = detail.attempts;
  const scored = attempts.filter((a) => a.scored);
  const totalMs = attempts.reduce((s, a) => s + (a.durationMs || 0), 0);

  if (!attempts.length) return <Empty>No attempts recorded.</Empty>;

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <h4 className="mb-2 text-sm font-semibold text-navy">Attempt history</h4>
        <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-100">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className={`w-12 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500`}>
                  #
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Raw score
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Percent
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Time
                </th>
                <th className="w-36 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  vs previous
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {attempts.map((a, i) => {
                // Compare against the previous SCORED attempt, not the previous
                // row: an abandoned attempt in between carries no score to
                // compare with, and treating it as one would invent a drop.
                const prev = scored.filter((s) => s.startedAt < a.startedAt).pop();
                return (
                  <tr key={a.instanceGuid} className={a.scored ? '' : 'text-gray-400'}>
                    <td className="px-4 py-2 text-sm tabular-nums text-gray-400 whitespace-nowrap">
                      {i + 1}
                    </td>
                    <td className="px-4 py-2 text-sm whitespace-nowrap">{fmtDate(a.startedAt)}</td>
                    <td className="px-4 py-2 text-sm tabular-nums text-gray-600 whitespace-nowrap">
                      {a.scored ? `${a.userScore}/${a.maxScore}` : '—'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {a.scored ? (
                        <ScorePill pct={a.pct} />
                      ) : (
                        <span className="text-xs italic">not finished</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                      {fmtMins(a.durationMs)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {!a.scored ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : !prev ? (
                        <span className="text-xs text-gray-400">first attempt</span>
                      ) : (
                        <Delta value={a.pct - prev.pct} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-100">
        <h4 className="mb-3 text-sm font-semibold text-navy">Summary</h4>
        <Sparkline points={scored.map((a) => Math.round(a.pct))} width={220} height={56} />
        <dl className="mt-4 space-y-2 text-sm">
          <SummaryRow label="Attempts" value={attempts.length} />
          <SummaryRow label="Questions" value={quiz.maxScoreExpected || '—'} />
          <SummaryRow
            label="First score"
            value={scored.length ? `${Math.round(scored[0].pct)}%` : '—'}
          />
          <SummaryRow
            label="Latest score"
            value={scored.length ? `${Math.round(scored[scored.length - 1].pct)}%` : '—'}
          />
          <SummaryRow
            label="Best score"
            value={scored.length ? `${Math.round(Math.max(...scored.map((a) => a.pct)))}%` : '—'}
          />
          <SummaryRow label="Total time" value={fmtMins(totalMs)} />
        </dl>
        <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
          <span className="text-sm text-gray-500">Improvement</span>
          <Delta value={quiz.improvement} />
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-semibold tabular-nums text-navy">{value}</dd>
    </div>
  );
}
